import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
  Backdrop,
  LinearProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateMoveService } from "../../redux/action/move-service";
import { unwrapResult } from "@reduxjs/toolkit";
import Header from "../header/Header";

const Insurance = () => {
  const [selectedOption, setSelectedOption] = useState<boolean | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { canonicalId,carrierCanonicalId } = useParams();
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const lastStep = 5;
  const handleInsuranceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelectedOption(event.target.value === "YES");
  };
  useEffect(() => {
    const updateLastStep = async () => {
      const moveServiceData = {
        canonical_id: canonicalId,
        last_step: lastStep,
      };
      await dispatch<any>(updateMoveService(moveServiceData))
        .then(unwrapResult)
        .then((res: any) => {});
    };

    if (canonicalId) {
      updateLastStep();
    }
  }, [canonicalId, dispatch, lastStep]);
  const navigate = useNavigate();
  const { businessName } = useParams();

  const handleContinue = async () => {
    if (selectedOption === null) {
      setError("Please select an insurance option");
      return;
    }

    try {
      setIsLoading(true);
      setLoadingMessage("Updating insurance selection...");
      const isInsurance = selectedOption;

      const moveServiceData = {
        canonical_id: canonicalId,
        isInsurance,
      };

      setTimeout(async () => {
        try {
          const res = await dispatch<any>(updateMoveService(moveServiceData));
          unwrapResult(res);
          setLoadingMessage("Redirecting to price calculation...");
          navigate(`/${businessName}/${carrierCanonicalId}/move-price/${canonicalId}`);
        } catch (err: any) {
          setError(err?.message || "Failed to update move service");
        } finally {
          setIsLoading(false);
        }
      }, 2000);
    } catch (err) {
      setIsLoading(false);
      setError("An unexpected error occurred");
    }
  };
  const handleBack = () => {
    navigate(-1);
  };
  return (
    <div>
      <Header />
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
          onClick={() => {
            setIsLoading(true);
          }}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Container
        sx={{
          width: "100%",
          maxWidth: "100%",
          height: isMobile ? "93vh" : "80vh",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          padding: isMobile ? "20px" : "20px",
          background: "#f2f0f0",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {error && (
          <Typography color="error" sx={{ mt: 2, textAlign: "center" }}>
            {error}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ mb: 1, color: theme.palette.text.secondary }}
            fontSize={isMobile ? "14px" : "18px"}
            textAlign="center"
            mb={2}
          >
            You will be provided insurance at $0.60 per pound included in your
            move. This is standard based on federal regulations.
            <br />
            Do you require additional insurance?
          </Typography>
          <Divider sx={{ background: "#D3D3D3", mb: 2 }} />
          <Box sx={{ paddingBottom: "20px" }}>
            <FormControl component="fieldset" sx={{ width: "100%" }}>
              <RadioGroup
                value={
                  selectedOption === null ? "" : selectedOption ? "YES" : "NO"
                }
                onChange={handleInsuranceChange}
                sx={{ gap: 2 }}
              >
                <FormControlLabel
                  value="YES"
                  control={
                    <Radio
                      sx={{
                        color: theme.palette.primary.main,
                        "&.Mui-checked": {
                          color: theme.palette.primary.main,
                        },
                      }}
                    />
                  }
                  label="Yes"
                  sx={{ width: "100%" }}
                />
                <FormControlLabel
                  value="NO"
                  control={
                    <Radio
                      sx={{
                        color: theme.palette.primary.main,
                        "&.Mui-checked": {
                          color: theme.palette.primary.main,
                        },
                      }}
                    />
                  }
                  label="No"
                  sx={{ width: "100%" }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: "20px",
            marginTop: "auto",
          }}
        >
          <Button
            variant="outlined"
            sx={{
              borderRadius: "20px",
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              padding: isMobile ? "8px 20px" : "8px 45px",
              textTransform: "none",
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "600",
            }}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            sx={{
              borderRadius: "20px",
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              padding: isMobile ? "8px 20px" : "8px 45px",
              textTransform: "none",
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "600",
              "&:hover": { backgroundColor: "#357ABD" },
            }}
            onClick={handleContinue}
            disabled={selectedOption === null || isLoading}
          >
            Continue
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default Insurance;
